import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Lock, ArrowRight, CheckCircle2, Play } from "lucide-react";

export default function HeroSection() {
  return (
    <section className="pt-28 sm:pt-36 lg:pt-48 pb-16 sm:pb-24 lg:pb-32 px-4 sm:px-6 lg:px-8 bg-zinc-50" data-testid="hero-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {/* Left: Text */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 text-sky-700 text-xs font-bold uppercase tracking-[0.15em] mb-8">
              <Lock className="w-3.5 h-3.5" />
              #1 AI-Powered Service as a Software Platform
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-950 tracking-tighter leading-[1.05] mb-6">
              Your Complete
              <span className="text-sky-700"> Business Resiliency</span> Platform
            </h1>
            <p className="text-lg sm:text-xl font-semibold text-slate-700 mb-4">
              Simple. Secure. Cost-Effective.
            </p>
            <p className="text-base text-slate-600 leading-relaxed mb-8 max-w-xl">
              Transform complex BCM processes into intuitive workflows. From BIA to risk assessments to recovery plans — everything you need for resilience excellence in one platform.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/request-demo">
                <Button size="lg" className="bg-sky-700 hover:bg-sky-800 h-12 px-8 text-base font-semibold shadow-lg shadow-sky-700/20 transition-all duration-300 hover:shadow-xl hover:shadow-sky-700/30" data-testid="hero-cta-btn">
                  Request Enquiry &amp; Demo
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-12 px-8 text-base font-semibold border-slate-300 hover:border-sky-700 hover:text-sky-700 transition-all duration-300"
                  data-testid="hero-demo-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Sign In
                </Button>
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-sm text-slate-500">
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                No credit card required
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Enterprise access · BCM-approved
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Instant demo access
              </span>
            </div>
          </div>

          {/* Right: Product Screenshot */}
          <div className="lg:col-span-7 relative lg:ml-[-2rem]">
            {/* Subtle glow */}
            <div className="absolute -inset-4 bg-sky-100/60 rounded-3xl blur-3xl"></div>
            {/* Main screenshot */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-slate-900/15 border border-slate-200">
              <img
                src="https://customer-assets.emergentagent.com/job_4ec0dd9d-0afa-4956-b016-9eb9fac2ee14/artifacts/acqtpn02_03_mission_control_dashboard.jpeg"
                alt="ResiliencyHUB Mission Control Dashboard"
                className="w-full h-auto block"
                style={{ marginBottom: "-40px", clipPath: "inset(0 0 40px 0)" }}
                data-testid="hero-product-screenshot"
              />
              {/* Overlay: ResiliencyHUB badge masking legacy logo in screenshot */}
              <div className="absolute top-0 left-0 bg-white border-r border-b border-slate-200 px-3 py-2 flex items-center gap-2 shadow-sm" style={{ width: '15%', minWidth: '120px' }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_cc74fbba-7d42-4e61-a0ea-eedd771fa683/artifacts/7xq5yifk_ResHubNEW1.1-removebg-preview.png"
                  alt="ResiliencyHUB"
                  className="h-7 w-auto"
                />
                <div className="leading-tight">
                  <div className="text-[11px] font-extrabold text-slate-900 tracking-tight whitespace-nowrap">Resiliency<span style={{ color: '#00BCD4' }}>HUB</span></div>
                </div>
              </div>
            </div>
            {/* Floating badge: AI-Powered */}
            <div className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 bg-white/70 backdrop-blur-xl backdrop-saturate-150 border border-white/40 shadow-lg rounded-xl px-4 py-2.5 z-10" data-testid="hero-badge-ai">
              <span className="text-sm font-bold text-sky-700">AI-Powered</span>
            </div>
            {/* Floating badge: ISO Compliant */}
            <div className="absolute -bottom-3 -left-3 sm:-bottom-4 sm:-left-4 bg-white/70 backdrop-blur-xl backdrop-saturate-150 border border-white/40 shadow-lg rounded-xl px-4 py-2.5 flex items-center gap-2 z-10" data-testid="hero-badge-iso">
              <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full"></div>
              <span className="text-sm font-bold text-slate-700">ISO 22301 Compliant</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
